<template>
  <div class="dropdown-box">
    <div class="dropdown-box__label">{{ label }}</div>
    <DropdownStandard
      :hasSearch="true"
      :options="options"
      :searchType="t('general__data__room__capacity-only').toLowerCase()"
      :title="title"
      :multiSelect="true"
      :isAllSelected="allSelected"
      @selectAll="selectAll"
      @select="select"
    />
  </div>
</template>

<script lang="ts" setup>
import DropdownStandard, { optionType } from '@/design-system/molecules/DropdownStandard.vue'
import { computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { ROOM_CAPACITY_FILTER } from '@/interfaces/roomcapacity.interface'
import { mergeIntervals } from '@/utils/merge-intervals'
import { intervalToLabel } from '@/intervalToLabel'
import { Interval } from '@/api-client/models/utilization/Contracts'

interface Props {
  selectedRoomCapacity: Array<ROOM_CAPACITY_FILTER>
  presentAsMultiSelect?: boolean
  optionsWithData?: (number | null)[]
}

const props = defineProps<Props>()
const { selectedRoomCapacity } = toRefs(props)
const { t } = useI18n()

const defaultFilters = computed<ROOM_CAPACITY_FILTER[]>(() => [
  {
    id: '1-10',
    value: [{ from: 1, to: 10 }],
    name: '1-10'
  },
  {
    id: '11-25',
    value: [{ from: 11, to: 25 }],
    name: '11-25'
  },
  {
    id: '26-50',
    value: [{ from: 26, to: 50 }],
    name: '26-50'
  },
  {
    id: '51-100',
    value: [{ from: 51, to: 100 }],
    name: '51-100'
  },
  {
    id: '>100',
    value: [{ from: 101 }],
    name: '>100'
  }
])
const options = computed<optionType[]>(() => {
  const selectedIds = selectedRoomCapacity.value.map((x) => x.id)
  return defaultFilters.value.map((x) => {
    const selected = selectedIds.includes(x.id)
    const disabled = !selected && !intervalContainsOptions(x.value[0], props.optionsWithData)
    const hasSubtitle = selected && !intervalContainsOptions(x.value[0], props.optionsWithData)
    const subtitle = hasSubtitle ? t('general__data__data-missing') : undefined
    return {
      ...x,
      name: intervalToLabel(t)(x.value[0]),
      selected,
      disabled,
      subtitle
    }
  })
})

const label = computed<string>(() => t('general__data__room__capacity'))
const title = computed<string>(() => {
  const selected = options.value.filter((el) => el.selected)
  if (selected && selected.length > 0) {
    const mergedIntervals = mergeIntervals(selected.map((x) => x.value[0]))
    if (mergedIntervals.length === 0) {
      return t('general__text__all')
    }
    return mergedIntervals.map(intervalToLabel(t)).join(', ')
  }
  return t('general__text__select') + ' ' + t('general__data__room__capacity-only').toLowerCase()
})

const allSelected = computed<boolean>(() => {
  const enabledCount = options.value.filter((x) => !x.disabled).length
  const selectedCount = options.value.filter((x) => x.selected).length
  return enabledCount === selectedCount
})

const emits = defineEmits<{ (e: 'selectionChanged', value: ROOM_CAPACITY_FILTER[]) }>()

/**
 * Checks whether or not the interval has at least one available option inside it.
 * If no available options are provided it returns true
 * @param interval the interval to check
 * @param optionsWithData the available options
 * @returns whether the interval contains at least one option
 */
function intervalContainsOptions(
  interval: Interval<number>,
  optionsWithData: (number | null)[] | undefined
): boolean {
  if (optionsWithData === undefined) return true
  return optionsWithData.some((value) => {
    if (value === null) return false

    return (!interval.from || value >= interval.from) && (!interval.to || value <= interval.to)
  })
}

function select(id) {
  const capacity = defaultFilters.value.find((c) => c.id === id)
  const index = selectedRoomCapacity.value.findIndex((x) => x.id === id)
  if (index === -1 && capacity) {
    emits('selectionChanged', [...selectedRoomCapacity.value, capacity])
  } else {
    const copy = [...selectedRoomCapacity.value]
    copy.splice(index, 1)
    emits('selectionChanged', copy)
  }
}

function selectAll() {
  if (!allSelected.value) {
    const enabled = options.value.filter((x) => !x.disabled)
    const enabledIds = enabled.map((x) => x.id)
    const selection = defaultFilters.value.filter((x) => enabledIds.includes(x.id))
    emits('selectionChanged', selection)
  } else {
    emits('selectionChanged', [])
  }
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.dropdown-box {
  display: flex;
  flex-direction: column;
  gap: 8px;
  &__label {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
  }
}
</style>
